import {Helper} from "@nolimitcity/promo-panel/bin/utils/Helper";
import {WINTYPE} from "@nolimitcity/promo-panel/bin/enums/ASEnums";
import {
    ActionSpinOptions,
    ActionSpinSound,
    ASWinData,
    PromoPanelGameConfiguration
} from "@nolimitcity/promo-panel/bin/interfaces/ASInterfaces";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {IGameData} from "./server/IData";
import {Game} from "./Game";
import {GameModeNames} from "./server/GameModeNames";
import {Sound} from "./sound/Sound";
import {BonusBuyPopUpContent} from "@nolimitcity/promo-panel/bin/feature/generic/BonusBuyPopUpContent";
import {Label} from "@nolimitcity/slot-launcher/bin/gui/labels/Label";
import {gsap} from "gsap";

export class GamePromoPanelConfig implements PromoPanelGameConfiguration {
    private triggeredFs: boolean;
    private featureName: string;

    public getActionSpinOptions(): ActionSpinOptions {
        return {
            gameName: "Tanked",
            hasActionSpin: true,
            graphics: {backgroundTextureName: "images/Intro_BG"},

            getParsedData: (response: IGameData, bet: number): ASWinData => {
                if (response.mode === GameMode.NORMAL) {
                    this.triggeredFs = false;
                }

                const parsedData: ASWinData = {
                    totalWin: response.accumulatedRoundWin,
                    wasFeatureBuy: response.wasFeatureBuy,
                    freeSpinTriggeredThisSpin: this.isFreeSpinTriggered(response),
                    totalSpinWinnings: this.getSpinWin(response),
                    isBigWin: this.isBigWin(response, bet),
                    winType: WINTYPE.NORMAL,
                    featureName: this.getFeatureName(response),
                    isRoundComplete: this.isRoundComplete(response),
                    isBonusEnd: this.isBonusEnd(response),
                    isWinCapHit: response.brokeTheBank,
                }
                return parsedData;
            }
        };
    }

    public replaceBonusBuyPopUpContent(orgContent: BonusBuyPopUpContent): PIXI.Container {
        // You can modify the orgContent to your liking, like in the simple text replace below. All params is public in orgContent, it's basically a PIXI container with some stuff in.
        // Or you can make you your own container with everything custom. You don't need to return a BonusBuyPopUpContent, it's enough with a container.
        // Use the priceString if you change the price text, it's already formatted correctly.
        if (orgContent.featureName === "GOD_MODE") {
            const warningText: Label = new Label(Game.apiPlugin.translations.translate("Warning: 1 Spin!"), orgContent.textLabel.getStyleClone()),
                scale: number = Math.min(560 / warningText.width, 1);
            warningText.scale.set(scale);
            warningText.anchor.set(0.5);
            warningText.position.set(0, 420);
            orgContent.addChild(warningText);
        }

        return orgContent;
    }

    public onActionSpinPlaySound(trigger: ActionSpinSound): void {
        if (trigger === ActionSpinSound.WIN) {
            Game.soundPlayer.playSound(Sound.AS_WIN, 1);
        } else if (trigger === ActionSpinSound.BIG_WIN) {
            Game.soundPlayer.playSound(Sound.AS_WIN_BIG, 1);
        }
    }

    public onPlayBonusInGame(data: IGameData): void {
        Game.apiPlugin.slotStates.resetGameStartedAndGameServerFlag();
        Game.model.onGame(data);
        this.handleBonusGame(data);
    }

    private resetAll(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();

        tl.add(() => {
            Game.resetExpandedElements().progress(1);
            Game.hasGridExpanded = false;
            Game.monsterCarriedSymbols = [[], [], [], []];
            Game.monsterExtraCarriedSymbols = [[], [], [], []];
            Game.reelCorners.resetLifeOfAllCorners(3);
        });
        return tl;
    }

    private handleBonusGame(data: IGameData): void {
        for (let i: number = 0; i < Game.ACTIVE_REELS_NUM; i++) {
            Game.reelsView.reelModels[i].allOut().progress(1);
        }
        Game.scatterInventory.hideSMeter(0.01);

        Game.keypadPlugin.setWin(data.accumulatedRoundWin, true, Game.isWinBelowStake());

        // #481
        const multiplier: number = data.multiplier;

        if (data.mayhemSpin) {
            // #481
            Game.multiplier.resetMultiplier(Math.max(multiplier - 10, 1));

            //#483
            Game.reelCorners.bonusBuyMayhemLifeReset();

            Game.featureIntro.showMayhemIntro(() => {
                Game.gameFlowController.start();

                Game.model.isMayHemAlreadyTriggered = true;
            }).timeScale(5);
        } else if (data.scatterCount > 3) {
            // #481
            Game.multiplier.resetMultiplier(Math.max(multiplier - 5, 1));

            Game.featureIntro.showSFSIntro(() => {
                Game.gameFlowController.start();
            }).timeScale(5);
        } else {
            // #481
            Game.multiplier.resetMultiplier(multiplier);

            for (let i: number = 0; i < Game.ACTIVE_REELS_NUM; i++) {
                Game.reelsView.reelModels[i].allOut().progress(1);
            }
            const fsIntroTl: gsap.core.Timeline = Game.featureIntro.showFSIntro(() => {
                Game.gameFlowController.start();
            }).timeScale(5);

            fsIntroTl.add(() => {
                const tl: gsap.core.Timeline = new gsap.core.Timeline();
                tl.add(this.resetAll());
                tl.add(() => {
                    let reelLen: number = 0, rowLen: number = 0;
                    for (let sym of data.reelsAfterAvalanche[0]) {
                        if (sym !== 'L') {
                            rowLen++;
                        } else {
                            break;
                        }
                    }
                    // get reels
                    for (let reel of data.reelsAfterAvalanche) {
                        if (reel[0] !== 'L') {
                            reelLen++;
                        } else {
                            break;
                        }
                    }
                    if ((reelLen - 6 > 0) || (rowLen - 6) > 0) {
                        Game.expandGrid((reelLen - 6), (rowLen - 6), true, true);
                    }
                });
            });
        }
    }

    private isBigWin(response: IGameData, bet: number): boolean {
        const win: number = this.getSpinWin(response);

        // #384
        if (response.wasFeatureBuy || Game.model.buyFeatureName === "BOOSTED_BET_ITEMS") {
            const winRatio: number = Helper.ceilToDecimals((win / Game.getBuyFeaturePrice(false)), 2);

            return (winRatio >= 1 && !Game.isSingleWinBelowStake());
        }

        return (Helper.ceilToDecimals((win / bet), 2) >= 15);
    }

    private getSpinWin(response: IGameData): number {
        return response.spinWin;
    }

    private isFreeSpinTriggered(response: IGameData): boolean {
        if ((response.mode == GameMode.NORMAL || response.mode == GameModeNames.NORMAL_AVALANCHE || response.mode == GameModeNames.FREESPIN_AVALANCHE)
            && response.nextMode == GameModeNames.FREESPIN && !this.triggeredFs) {
            this.triggeredFs = true;
            return true;
        }

        return false;
    }

    private getFeatureName(response: IGameData): string {
        if (response.mayhemSpin) {
            this.featureName = "MAYHEM SPINS";
        } else if (response.scatterCount === 3) {
            this.featureName = "CARNAGE SPINS";
        } else if (response.scatterCount === 4) {
            this.featureName = "CARNAGE ROYALE";
        } else if (response.mode.indexOf(GameMode.FREESPIN) === -1) {
            this.featureName = "";
        }

        return this.featureName;
    }

    private isRoundComplete(response: IGameData): boolean {
        return ([GameMode.FREESPIN, GameMode.NORMAL].indexOf(response.nextMode) > -1);
    }

    private isBonusEnd(response: IGameData): boolean {
        return ((response.nextMode === GameMode.NORMAL) && (response.mode === GameMode.FREESPIN || response.mode === GameModeNames.FREESPIN_AVALANCHE));
    }
}